import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Channel } from 'pusher-js';
import { UntypedFormControl } from '@angular/forms';
import { ID } from '@datorama/akita';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Md5 } from 'ts-md5/dist/md5';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable } from 'rxjs';
import { filter, first, map, startWith } from 'rxjs/operators';

import { ActionBar } from '@component/action-bar';
import { ConfirmationModalComponent, ConfirmationModalData } from '@component/confirmation-modal';
import { ContentEditorDialog } from '@app/shared/components/dialogs/content-editor/content-editor.dialog';
import { FormsService } from '@state/forms';
import { DocumentService, EditorState, FormService } from '@app/shared/services';
import { FilestackClient } from '@app/shared/services/vendor';
import {
  ElementObject,
  ElementObjectType,
  Form,
  FormDisplayType,
  FormElement,
  FormFieldOption,
  FormRepository,
  FormSection,
  FormVariable,
  SectionType,
  SelectableType,
  SigningActorRoleType,
  VisibilityKey,
  VisibilityValue,
  WidgetType
} from '@state/form';
import { getVariablesFrom } from '@helpers/string';
import { ImportSectionsModalComponent } from '@app/shared/components/sidebar/import-sections-modal';
import { PusherService } from '@state/pusher';
import { UserQuery } from '@state/user';
import { VariableCreateDialog } from '@app/shared/components/dialogs/variable-create/variable-create.dialog';
import { MaskFunctionOptions } from '@helpers/masks';

enum FormattingKeys {
  Accept = 'accept',
  Autocomplete = 'autocomplete',
  CheckboxSpecialId = 'checkboxSpecialId',
  CheckboxSpecialValue = 'checkboxSpecialValue',
  Direction = 'direction',
  ElementClass = 'elementClass',
  ExpandableContent = 'expandableContent',
  GroupId = 'groupId',
  GroupSpan = 'groupSpan',
  LetterSpacing = 'letterSpacing',
  MaskFunction = 'maskFunction',
  Placeholder = 'placeholder',
  QuestionLocation = 'questionLocation',
  RichEditor = 'richEditor',
  TransformPattern = 'transformPattern',
  TransformReplacement = 'transformReplacement',
  TransformTrim = 'transformTrim',
  ReportHideColumn = 'reportHideColumn',
  ReportColumnLabel = 'reportColumnLabel'
}

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-sidebar',
  styleUrls: ['./sidebar.component.scss'],
  templateUrl: './sidebar.component.html'
})
export class SidebarComponent implements OnInit, OnDestroy {
  // High order selectable options
  @ViewChild('documentOptions', { static: true }) documentOptions: TemplateRef<any>;
  @ViewChild('formOptions', { static: true }) formOptions: TemplateRef<any>;
  @ViewChild('rtdOptions', { static: true }) rtdOptions: TemplateRef<any>;
  @ViewChild('sectionOptions', { static: true }) sectionOptions: TemplateRef<any>;

  // Element options
  @ViewChild('contentOptions', { static: true }) contentOptions: TemplateRef<any>;

  // Field Options
  @ViewChild('booleanFieldOptions', { static: true }) booleanFieldOptions: TemplateRef<any>;
  @ViewChild('choiceFieldOptions') choiceFieldOptions: TemplateRef<any>;
  @ViewChild('datepickerFieldOptions', { static: true }) datepickerFieldOptions: TemplateRef<any>;
  @ViewChild('documentFieldOptions', { static: true }) documentFieldOptions: TemplateRef<any>;
  @ViewChild('enumerationFieldOptions', { static: true }) enumerationFieldOptions: TemplateRef<any>;
  @ViewChild('fixedTextFieldOptions', { static: true }) fixedTextFieldOptions: TemplateRef<any>;
  @ViewChild('signatureFieldOptions', { static: true }) signatureFieldOptions: TemplateRef<any>;
  @ViewChild('textboxFieldOptions', { static: true }) textboxFieldOptions: TemplateRef<any>;

  // Special Options
  @ViewChild('advancedOptions', { static: true }) advancedOptions: TemplateRef<any>;
  @ViewChild('pageObjectOptions') pageObjectOptions: TemplateRef<any>;

  @Input() editorWorkspaceType: string;

  actionBar: ActionBar;
  channel: Channel;
  readonly FormDisplayType = FormDisplayType;

  private debounceId: NodeJS.Timeout;
  private readonly DEBOUNCE_DELAY: number = 400;
  private readonly NON_PRINTABLE_KEY_CODE = [
    'ArrowDown',
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight',
    'Meta',
    'Enter',
    'Shift',
    'Escape'
  ];

  user$ = this.userQuery.select('user');
  objectKeys = Object.keys;
  organizationKeys: string[] = [];
  sectionType = SectionType;
  variablesMissed = [];

  readonly MaskFunctionOptions = MaskFunctionOptions;

  readonly FORMATTING_KEYS: FormattingKeys[] = [
    FormattingKeys.Accept,
    FormattingKeys.Autocomplete,
    FormattingKeys.CheckboxSpecialId,
    FormattingKeys.CheckboxSpecialValue,
    FormattingKeys.Direction,
    FormattingKeys.ElementClass,
    FormattingKeys.GroupId,
    FormattingKeys.GroupSpan,
    FormattingKeys.LetterSpacing,
    FormattingKeys.MaskFunction,
    FormattingKeys.Placeholder,
    FormattingKeys.QuestionLocation,
    FormattingKeys.RichEditor,
    FormattingKeys.TransformPattern,
    FormattingKeys.TransformReplacement,
    FormattingKeys.TransformTrim,
    FormattingKeys.ReportHideColumn,
    FormattingKeys.ReportColumnLabel
  ];
  filteredFormattingKeys$: Observable<FormattingKeys[]>;
  formattingKeyControl = new UntypedFormControl();

  // Do not alpha sort, this should render in this order
  // TODO: Should refactor so it's an actual collection and not a hash

  /* eslint-disable */
  readonly SIGNING_ACTOR_ROLE_TYPES = {
    'Primary Signer 1': SigningActorRoleType.Primary1,
    'Primary Signer 2': SigningActorRoleType.Primary2,
    'Primary Signer 3': SigningActorRoleType.Primary3,
    'Primary Signer 4': SigningActorRoleType.Primary4,
    'Primary Signer 5': SigningActorRoleType.Primary5,
    'Primary Signer 6': SigningActorRoleType.Primary6,
    'Countersigner 1': SigningActorRoleType.Counter1,
    'Countersigner 2': SigningActorRoleType.Counter2,
    'Countersigner 3': SigningActorRoleType.Counter3,
    'Countersigner 4': SigningActorRoleType.Counter4,
    'Countersigner 5': SigningActorRoleType.Counter5,
    'Countersigner 6': SigningActorRoleType.Counter6
  };
  /* eslint-enable */

  readonly TEMPLATE_FIELD_TYPES = {
    // eslint-disable-next-line id-blacklist
    boolean: 'boolean',
    date_time: 'datepicker',
    document: 'document',
    enumeration: 'enumeration',
    fixed_text: 'fixedText',
    numeric: 'textbox',
    signature: 'signature',
    text: 'textbox'
  };

  readonly BOOLEAN_TITLES = {
    checkbox: 'Checkbox',
    slide_toggle: 'Slide Toggle'
  };

  readonly ENUMERATION_TITLES = {
    custom: 'Custom Select',
    multi_select: 'Dropdown',
    radio: 'Radio'
  };

  readonly TEXT_TITLES = {
    hidden: 'Hidden Input',
    password: 'Password',
    text_area: 'Multiline Text',
    text_input: 'Text Input'
  };

  readonly DATE_TYPES = {
    'M/D/YY': '%-d/%-m/%y',
    'M/D/YYYY': '%-d/%-m/%Y',
    'MM/DD/YYYY': '%m/%d/%Y',
    'MM/DD/YYYY HH:MM': '%m/%d/%Y %H:%M',
    'Mon Day, Year': '%b%e, %Y',
    'Month Day, Year': ' %B %e, %Y',
    'YYYY-MM-DD': '%Y-%m-%d',
    'YYYY-MM-DD HH:MM': '%Y-%m-%d %H:%M'
  };

  readonly WIDGET_TYPES = {
    checkbox: { icon: 'check_box', title: 'Checkbox' },
    content: { icon: 'code', title: 'Content Block' },
    custom: { icon: 'dashboard', title: 'Custom Select' },
    date_picker: { icon: 'event', title: 'Date Picker' },
    document: { icon: 'cloud_upload', title: 'Document Upload' },
    fixed_text: { icon: 'format_shapes', title: 'Fixed Text' },
    form: { icon: 'note_add', title: 'Form' },
    hidden: { icon: 'visibility_off', title: 'Hidden Input' },
    multi_select: { icon: 'arrow_drop_down_circle', title: 'Dropdown' },
    number_input: { icon: 'money', title: 'Number Input' },
    password: { icon: 'more_horiz', title: 'Password' },
    radio: { icon: 'radio_button_checked', title: 'Radio Group' },
    signature: { icon: 'gesture', title: 'Signature Block' },
    slide_toggle: { icon: 'toggle_on', title: 'Slide Toggle' },
    text_area: { icon: 'notes', title: 'Multiline Input' },
    text_input: { icon: 'text_format', title: 'Text Input' }
  };

  newConfiguration: any = {};

  fieldOptionToAdd: FormFieldOption = {
    id: undefined,
    raw_html: undefined,
    value: undefined
  };

  groups: string[] = [];

  _visibilityValue: string;

  constructor(
    private filestackClient: FilestackClient,
    private formRepository: FormRepository,
    private formsService: FormsService,
    private userQuery: UserQuery,
    public dialog: MatDialog,
    public documentService: DocumentService,
    public editorState: EditorState,
    public formService: FormService,
    public snackBar: MatSnackBar,
    public pusherService: PusherService
  ) {}

  ngOnInit() {
    this.filteredFormattingKeys$ = this.formattingKeyControl.valueChanges.pipe(
      startWith(''),
      map((value: string) => this.filterFormattingKeys(value))
    );

    this.formRepository.form$
      .pipe(first((form) => !!form))
      .subscribe({
        next: () => {
          this.setActionBar();
          this.bindPusherChannel();
        }
      });
  }

  ngOnDestroy(): void {
    this.channel.unbind();
  }

  parseForm(): void {
    this.groups = [];
    this.formService.form.sections.forEach((section) => {
      section.elements.forEach((element) => {
        if (element.configuration.groupId && !this.groups.includes(element.configuration.groupId)) {
          this.groups.push(element.configuration.groupId);
        }
      });
    });
  }

  bindPusherChannel(): void {
    let channelId: string = Md5.hashStr(`forms/${this.formService.form.id}`);
    this.channel = this.pusherService.pusher.subscribe(channelId);
    this.channel.bind('form_update', (data) => {
      // Only show snack bar if you didn't make the edit
      if (data.sender_request_id !== this.formService.sender_request_id) {
        let snackBarRef = this.snackBar.open(
          'Updates to this form have been made. Please refresh this page to get latest',
          'Reload',
          {
            duration: 259200,
            horizontalPosition: 'center'
          }
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        snackBarRef.onAction().subscribe({ next: () => this.formService.getForm(this.formService.form.id) });
      }
    });
  }

  setActionBar(): void {
    let buttons = [];
    let menu = [];

    if (this.formService.form) {
      buttons.push(
        {
          label: 'Preview',
          newTab: true,
          routerLink: ['/builder', 'form', this.formService.form.id, 'preview']
        },
        {
          action: () => {
            this.saveField({ selectedItem: this.editorState.selectedItem });
          },
          disabled: (): any => this.editorState.selectedItem.isSubmitting,
          label: this.editorState.selectedItem.isSubmitting ? 'Saving...' : 'Save',
          primary: true
        }
      );

      menu.push({
        action: () => this.openImportSectionsModal(),
        label: 'Import Sections'
      });

      if (
        !this.formService.form.vehicle_id &&
        this.formService.form.active &&
        this.formService.form.assignable_to !== 'investment'
      ) {
        menu.push({
          label: 'Share',
          newTab: true,
          queryParams: this.shareParams,
          routerLink: ['e', this.formService.form.organization.entity_id, 'share']
        });
      }
    }

    this.actionBar = { buttons, menu };

    this.parseForm();
  }

  get shareParams(): any {
    let queryParams = {
      shareable_type: this.editorWorkspaceType
    };

    if (this.formService.form) {
      queryParams['scope'] = this.formService.form.vehicle_id ? 'vehicle' : 'organization';
      if (queryParams['scope'] === 'vehicle') {
        queryParams['scope_id'] = this.formService.form.vehicle_id;
      }
      queryParams['shareables'] = this.formService.form.id;
      queryParams['queryParams'] = 'form';
    }

    return queryParams;
  }

  addVisibilityValue(): void {
    this.editorState.selectedItem.visibility_values.push(this._visibilityValue);
    this.saveField({ selectedItem: this.editorState.selectedItem });
    this._visibilityValue = '';
  }

  dropEnumerationRow({ previousIndex, currentIndex }: { previousIndex: number; currentIndex: number }): void {
    let selectedItem = this.editorState.selectedItem as FormElement;

    moveItemInArray(selectedItem.element_object.field_options, previousIndex, currentIndex);
    this.updatePositions(selectedItem.element_object.field_options);
    this.saveField({ selectedItem });
  }

  deleteField(): void {
    const element = this.editorState.selectedItem as FormElement;
    this.formService.removeElement(element.form_section_id, element, {
      success: (data) => {
        if (data.document) {
          this.documentService.document = data.document;
        }

        this.documentService.document.selectableType = SelectableType.Document;
        this.editorState.selectedItem = this.documentService.document;
        delete this.editorState.selectedItemPageObject;
      }
    });
  }

  get childTemplate(): TemplateRef<any> {
    if (!this.editorState.selectedItem) return;

    let type: string;

    switch (this.editorState.selectedItem.selectableType) {
      case SelectableType.Document:
      case SelectableType.Form:
      case SelectableType.Rtd:
      case SelectableType.Section:
        type = this.editorState.selectedItem.selectableType;
        break;
      case SelectableType.Element:
        switch (this.editorState.selectedItem.element_object_type) {
          case ElementObjectType.Content:
            type = ElementObjectType.Content;
            break;

          default:
            type = `${
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              this.TEMPLATE_FIELD_TYPES[this.editorState.selectedItem.element_object['data_type']]
            }Field`;
            break;
        }
        break;
      case SelectableType.PageObject:
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        type = `${this.TEMPLATE_FIELD_TYPES[this.editorState.selectedItem.element_object['data_type']]}Field`;
        break;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this[`${type}Options`];
  }

  onVariableKeyEvent(event: any, forVisibility = false): void {
    if (
      this.NON_PRINTABLE_KEY_CODE.indexOf(event.key) > -1 ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    clearTimeout(this.debounceId);

    this.debounceId = setTimeout(() => {
      if (event.target.value && event.target.value.length >= 2) {
        this.formService.searchCustomVariables(event.target.value, '', '', '', forVisibility);
      }
    }, 750);
  }

  onVariableBlur(): void {
    if (this.editorState.selectedItem.element_object.custom_variable_key === '') {
      this.editorState.selectedItem.element_object.custom_variable_id = null;
      this.saveField({ selectedItem: this.editorState.selectedItem });
      return;
    }

    let variable = this.formService.formVariables.filter(
      (v) => v.key === this.editorState.selectedItem.element_object.custom_variable_key
    )[0];

    if (variable) {
      this.editorState.selectedItem.element_object.custom_variable_key = variable.key;
      this.editorState.selectedItem.element_object.custom_variable_id = variable.id;
    }

    this.saveField({ selectedItem: this.editorState.selectedItem });
  }

  checkAddVisibilityVariable(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    this.addVisibilityValue();
  }

  checkFormVariableVisibilityIsConditional(): string {
    let formVariableVisibilityType = 'conditional';

    if (
      !this.editorState.selectedItem.visibility_key &&
      this.editorState.selectedItem.conditionalVisibility !== 'conditional'
    ) {
      formVariableVisibilityType = VisibilityValue.Always;
    }

    return formVariableVisibilityType;
  }

  clearFormatFunction(form_element: FormElement): void {
    delete form_element.configuration.formatFunction;
  }

  instanceOfDocument(item: any): boolean {
    return item?.selectableType === SelectableType.Document;
  }

  instanceOfElement(item: Form | FormElement | FormSection): boolean {
    return item?.selectableType === SelectableType.Element;
  }

  instanceOfForm(item: Form | FormElement | FormSection): boolean {
    return item?.selectableType === SelectableType.Form;
  }

  instanceOfGroupableObject(item: Form | FormElement | FormSection): boolean {
    return ((item?.selectableType === SelectableType.Element ||
      item?.selectableType === SelectableType.PageObject) &&
      item.element_object_type === ElementObjectType.FormField &&
      this.instanceOfElementOrPageObject(item) && [
        WidgetType.DatePicker,
        WidgetType.Document,
        WidgetType.Password,
        WidgetType.SlideToggle,
        WidgetType.TextInput
      ].includes(item?.element_object?.widget_type));
  }

  // Need to use this when selecting a FormElement that has an associated page object (in Form view) or
  // when selecting a page object (in Document View)
  instanceOfOrHasPageObject(item: Form | FormElement | FormSection): boolean {
    return this.instanceOfPageObject(item) ||
      (item?.selectableType === SelectableType.Element && !!item.element_object.page_object_id);
  }

  instanceOfPageObject(item: Form | FormElement | FormSection): boolean {
    return item?.selectableType === SelectableType.PageObject;
  }

  instanceOfRTD(item: Form | FormElement | FormSection): boolean {
    return item?.selectableType === SelectableType.Rtd;
  }

  instanceOfSaveable(item: Form | FormElement | FormSection): boolean {
    return (
      this.instanceOfDocument(item) ||
      this.instanceOfElement(item) ||
      this.instanceOfForm(item) ||
      this.instanceOfRTD(item) ||
      this.instanceOfSection(item) ||
      this.instanceOfPageObject(item)
    );
  }

  instanceOfSection(item: Form | FormElement | FormSection): boolean {
    return item?.selectableType === SelectableType.Section;
  }

  instanceOfSignature(item: Form | FormElement | FormSection): boolean {
    return 'element_object' in item ? item.element_object.widget_type === WidgetType.Signature : false;
  }

  instanceOfText(item: Form | FormElement | FormSection): boolean {
    return 'element_object' in item ? item.element_object.widget_type === WidgetType.TextInput : false;
  }

  instanceOfFixedText(item: Form | FormElement | FormSection): boolean {
    return 'element_object' in item ? item.element_object.widget_type === WidgetType.FixedText : false;
  }

  instanceOfTextOrFixedText(item: Form | FormElement | FormSection): boolean {
    return this.instanceOfText(item) || this.instanceOfFixedText(item);
  }

  showAdvancedOptions(item: Form | FormElement | FormSection): boolean {
    return this.instanceOfElement(item) || this.instanceOfSection(item) || this.instanceOfPageObject(item);
  }

  instanceOfElementOrPageObject(item: Form | FormElement | FormSection): boolean {
    return this.instanceOfElement(item) || this.instanceOfPageObject(item);
  }

  onChangeFormVariableVisibilitySelect(event: { value: string }): void {
    this.editorState.selectedItem.conditionalVisibility = event.value;

    switch (this.editorState.selectedItem.conditionalVisibility) {
      case VisibilityValue.Always:
        this.editorState.selectedItem.visibility_key = null;
        this.editorState.selectedItem.visibility_values = [];
        break;
      case VisibilityValue.IndividualJoint:
        this.editorState.selectedItem.visibility_key = VisibilityKey.InvestorType;
        this.editorState.selectedItem.visibility_values = [VisibilityValue.Individual, VisibilityValue.Joint];
        break;
      case VisibilityValue.EntityTrust:
        this.editorState.selectedItem.visibility_key = VisibilityKey.InvestorType;
        this.editorState.selectedItem.visibility_values = [VisibilityValue.Entity, VisibilityValue.Trust];
        break;
      case VisibilityValue.Joint:
        this.editorState.selectedItem.visibility_key = VisibilityKey.InvestorType;
        this.editorState.selectedItem.visibility_values = [VisibilityValue.Joint];
    }
  }

  openContentDialog(raw_html: string): void {
    const dialogRef = this.dialog.open(ContentEditorDialog, {
      data: { content: raw_html },
      height: 'calc(100vh - 10rem)',
      maxWidth: 'none',
      width: 'calc(100vw - 10rem)'
    });

    dialogRef
      .afterClosed()
      .pipe(filter((result: string) => !!result))
      .subscribe({
        next: (result) => {
          let selectedItem;

          if (this.editorState.selectedItem.selectableType === SelectableType.Section) {
            selectedItem = this.editorState.selectedItem as FormSection;
            selectedItem.content.raw_html = result;
          } else if (this.editorState.selectedItem.selectableType === SelectableType.Element) {
            selectedItem = this.editorState.selectedItem;
            selectedItem.element_object.raw_html = result;
          }

          this.saveField({ selectedItem });
        }
      });
  }

  updateLabel(element: any): void {
    let field: ElementObject = element.element_object;
    this.documentService.setElementObjectLabel(field);
  }

  updateReassignable(event: MatSelectChange): void {
    if (event.value !== 'investment') this.editorState.selectedItem.reassignable = false;
  }

  updatePositions(objs: any[]): void {
    for (let [index, obj] of objs.entries()) {
      obj.position = index;
    }
  }

  removeVisibilityValue(value: string): void {
    let visibility_values = this.editorState.selectedItem.visibility_values;
    for (let i = 0; i < visibility_values.length; i++) {
      if (visibility_values[i] === value) visibility_values.splice(i, 1);
    }

    this.saveField({
      avoidDelay: true,
      selectedItem: this.editorState.selectedItem
    });
  }

  showSuccessMessageIf =
    (shouldBeShown: boolean) =>
    (message: string): void => {
      if (shouldBeShown) this.snackBar.open(message);
    };

  saveField({
    selectedItem,
    avoidDelay,
    resetSelectedItem,
    isManualSave
  }: {
    selectedItem: any;
    avoidDelay?: boolean;
    resetSelectedItem?: boolean;
    isManualSave?: boolean;
    callback?: void;
  }): void {
    if (!this.instanceOfSaveable(this.editorState.selectedItem)) return;

    clearTimeout(this.debounceId);
    let timeout: number = avoidDelay ? 0 : this.DEBOUNCE_DELAY;
    this.editorState.selectedItem.isSubmitting = true;
    this.setActionBar();

    this.debounceId = setTimeout(() => {
      let { selectableType } = selectedItem;
      const showSuccessMessageIfManualSave = this.showSuccessMessageIf(isManualSave);

      switch (selectableType) {
        case SelectableType.Document:
          return this.formService.putForm(
            {
              document: {
                filename: this.documentService.document.title,
                title: this.documentService.document.title
              }
            },
            {
              success: () => {
                this.editorState.selectedItem.isSubmitting = false;
                this.setActionBar();
                showSuccessMessageIfManualSave('Form updated successfully');
              }
            },
            false
          );
        case SelectableType.Form:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return this.formService.putForm(
            selectedItem,
            {
              success: () => {
                this.editorState.selectedItem.isSubmitting = false;
                this.setActionBar();
                showSuccessMessageIfManualSave('Form updated successfully');
              }
            },
            false
          );
        case SelectableType.Rtd:
          let element = selectedItem.sections[0].elements[0];
          this.allVariableKeysExists(element.element_object.raw_html);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return this.formService.putElements(
            selectedItem.sections[0].id,
            [element],
            {
              success: () => {
                this.editorState.selectedItem.isSubmitting = false;
                this.setActionBar();
                showSuccessMessageIfManualSave('Document updated successfully');
              }
            },
            false
          );
        case SelectableType.Section:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return this.formService.putSections(
            [selectedItem],
            {
              success: () => {
                this.editorState.selectedItem.isSubmitting = false;
                this.setActionBar();
                showSuccessMessageIfManualSave('Section updated successfully');
              }
            },
            false
          );
        case SelectableType.Element:
        case SelectableType.PageObject:
          let formElement = this.formService.queryFormElementById(selectedItem.id);

          if (formElement.element_object.page_object_id) {
            let pageObjectId = formElement.element_object.page_object_id;
            // Need to force a dimensional change in order rerender element
            if (this.documentService.mapperObjects[pageObjectId]) {
              this.documentService.mapperObjects[pageObjectId].width += 0.00000000001;
              this.documentService.mapperObjects[pageObjectId].mapperComponent.renderCanvas();
            }
          }

          this.formService.putElements(
            selectedItem.form_section_id,
            [selectedItem],
            {
              success: () => {
                this.editorState.selectedItem.isSubmitting = false;

                if (formElement && !resetSelectedItem) {
                  if (this.editorState.selectedItem.element_object) {
                    this.editorState.selectedItem.element_object.field_options =
                      formElement.element_object.field_options;
                  }
                  this.editorState.selectedItem.configuration = formElement.configuration;
                }

                showSuccessMessageIfManualSave('Element updated successfully');
                this.setActionBar();
              }
            },
            false
          );
      }
    }, timeout);
  }

  publishForm(): void {
    this.formService.publishForm({
      success: () => {
        // manually setting active to true since response is not a serialized form
        this.editorState.selectedItem.active = this.formService.form.active = true;
        this.setActionBar();
      }
    });
  }

  unlockForm(): void {
    let data: ConfirmationModalData = {
      message: `Are you sure you want to unlock this form? Once unlocked,
                <b>only you will be able to make edits to this form</b>.
                In order to allow others to edit you must lock the form again.`,
      positiveButtonLabel: 'Unlock Form for Editing',
      title: 'Unlock Form'
    };

    let dialogRef = this.dialog.open(ConfirmationModalComponent, { data });

    dialogRef
      .afterClosed()
      .pipe(filter((confirmed: boolean) => confirmed))
      .subscribe({
        next: () => {
          this.formService.lockForm();
        }
      });
  }

  lockForm(): void {
    let data: ConfirmationModalData = {
      message:
        'Are you sure you want to lock this form? Once locked, others will be able to unlock and make edits.',
      positiveButtonLabel: 'Lock Form',
      title: 'Lock Form'
    };

    let dialogRef = this.dialog.open(ConfirmationModalComponent, { data });
    dialogRef.afterClosed().subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) {
          this.formService.unlockForm();
        }
      }
    });
  }

  removeFieldOption(selectedItem: any, fieldOption: FormFieldOption): void {
    this.formService.removeFieldOption(selectedItem.form_section_id, selectedItem.id, fieldOption.id, {
      success: () => {
        let index = selectedItem.element_object.field_options.findIndex(
          (element) => fieldOption.id === element.id
        );

        selectedItem.element_object.field_options.splice(index, 1);
      }
    });
  }

  editFieldOption(event: any, selectedItem: any, formFieldOption: FormFieldOption): void {
    event.target.select();
    this.resetAllFieldOptionEditing(selectedItem);

    formFieldOption.isEditing = true;
  }

  resetAllFieldOptionEditing(selectedItem: any): void {
    selectedItem.element_object.field_options.forEach((element) => {
      element.isEditing = false;
    });
  }

  onFieldOptionKeyPress(event: KeyboardEvent, selectedItem: any, formFieldOption: FormFieldOption): void {
    if (!formFieldOption.raw_html || !formFieldOption.value) return;
    if (event.key === 'Enter') {
      this.addOrUpdateFieldOption(selectedItem, formFieldOption);
      this.selectPreviousField(event.currentTarget as HTMLInputElement);
    }
  }

  addOrUpdateFieldOption(selectedItem: any, fieldOption: FormFieldOption, reset: boolean = true): void {
    if (reset) {
      selectedItem.element_object.field_options.forEach((element) => {
        element.isEditing = false;
      });
    }

    if (fieldOption.id) {
      this.formService.putFieldOption(
        selectedItem.form_section_id,
        selectedItem.id,
        fieldOption.id,
        fieldOption
      );
    } else {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      fieldOption.position = selectedItem.element_object.field_options.length + 1;

      this.formService.postFieldOption(selectedItem.form_section_id, selectedItem.id, fieldOption, {
        success: (data) => {
          selectedItem.element_object.field_options.push(data);
        }
      });

      this.fieldOptionToAdd = {
        id: null,
        raw_html: null,
        value: null
      };
    }
  }

  onFormattingOptionKeyPress(event: KeyboardEvent, selectedItem: any, formFieldOption: any): void {
    let formFieldOptionToUpdate: FormFieldOption = { ...formFieldOption };
    if (!formFieldOptionToUpdate.key || !formFieldOptionToUpdate.value) return;
    if (event.key === 'Enter') {
      this.addOrUpdateConfig(selectedItem, formFieldOptionToUpdate);
      this.selectPreviousField(event.currentTarget as HTMLInputElement);
    }
  }

  addOrUpdateConfig(selectedItem: any, option: any): void {
    option.isEditing = false;
    this.editorState.selectedItem.configuration[option.key] = option.value;

    this.saveField({
      avoidDelay: true,
      resetSelectedItem: true,
      selectedItem: this.editorState.selectedItem
    });

    this.newConfiguration = {};
  }

  removeConfig(selectedItem: any, option: any): void {
    option.isEditing = false;
    delete this.editorState.selectedItem.configuration[option.key];

    this.saveField({
      avoidDelay: true,
      resetSelectedItem: true,
      selectedItem: this.editorState.selectedItem
    });

    this.newConfiguration = {};
  }

  editConfig(event: any, selectedItem: any, config: any): void {
    event.target.select();
    this.resetAllFieldOptionEditing(selectedItem);

    config.isEditing = true;
  }

  selectPreviousField(el: HTMLInputElement): void {
    let previousField: HTMLInputElement = el.parentElement.parentElement.querySelector('input:first-of-type');
    previousField.focus();
  }

  openCustomOptionDialog(selectedItem: any, fieldOption: FormFieldOption): void {
    const dialogRef = this.dialog.open(ContentEditorDialog, {
      data: { content: fieldOption.raw_html },
      height: 'calc(100vh - 10rem)',
      maxWidth: 'none',
      width: 'calc(100vw - 10rem)'
    });

    dialogRef
      .afterClosed()
      .pipe(filter((result: string) => !!result))
      .subscribe({
        next: (result) => {
          fieldOption.raw_html = result;
          this.addOrUpdateFieldOption(selectedItem, fieldOption);
        }
      });
  }

  // End Field Options

  openCreateVariableModal(key: string): void {
    this.documentService.inputSelected = true;
    const dialogRef = this.dialog.open(VariableCreateDialog, {
      data: { key },
      width: '500px'
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result) return;

        this.formService.formVariables.push(result as FormVariable);
        this.editorState.selectedItem.element_object.custom_variable_key = result.key;
        this.editorState.selectedItem.element_object.custom_variable_id = result.id;

        this.saveField({
          avoidDelay: true,
          selectedItem: this.editorState.selectedItem
        });
      }
    });
  }

  openHelpContentDialog(selectedItem: any): void {
    const dialogRef = this.dialog.open(ContentEditorDialog, {
      data: { content: selectedItem.element_object.tooltip },
      height: 'calc(100vh - 10rem)',
      maxWidth: 'none',
      width: 'calc(100vw - 10rem)'
    });

    dialogRef
      .afterClosed()
      .pipe(filter((result: string) => !!result))
      .subscribe({
        next: (result) => {
          selectedItem.element_object.tooltip = result;
          this.saveField({
            avoidDelay: true,
            selectedItem: this.editorState.selectedItem
          });
        }
      });
  }

  openImportSectionsModal(): void {
    let { id: form_id } = this.formService.form;
    let data = { form_id };

    this.dialog
      .open(ImportSectionsModalComponent, {
        data,
        panelClass: 'fl-import-sections-dialog'
      })
      .afterClosed()
      .subscribe({ next: (form: Form) => {
        if (form) this.formService.form = form;
      } });
  }

  downloadDocument(): void {
    this.documentService.getDownloadToken(this.editorState.selectedItem.id);
  }

  get currentSection(): FormSection {
    let sectionId = this.editorState.selectedItem.form_section_id;
    return this.formService.form.sections.find((section: FormSection) => section.id === sectionId);
  }

  changePage(event: MatSelectChange): void {
    let document_page_id: ID = event.value;
    let params = { document_page_id };
    let { id, document_page_id: source_id } = this.editorState.selectedItemPageObject;

    this.documentService.updatePageObject$(this.documentService.document.id, id, params).subscribe({
      next: (pageObject) => this.documentService.movePageObject(pageObject, document_page_id, source_id)
    });
  }

  changeSection(event: MatSelectChange): void {
    let position = this.formService.form.sections.find((section) => section.id === event.value.id).elements
      .length;
    this.editorState.selectedItem.form_section_id = event.value.id;
    this.editorState.selectedItem.position = position;
    this.saveField({ selectedItem: this.editorState.selectedItem });
  }

  updateFieldRole(field: any): Observable<any> {
    if (field.element_object.page_object_id) {
      let pageObjectId = field.element_object.page_object_id;
      let counterSignable = field.role.includes('counter');
      let mapperObject = this.documentService.mapperObjects[pageObjectId];
      if (mapperObject) {
        mapperObject.counterSign = counterSignable;

        if (mapperObject.outerBox) {
          mapperObject.outerBox.counterSign = counterSignable;
        }

        mapperObject.width += 0.00000000001;
        mapperObject.mapperComponent.renderCanvas();
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.formService.putElements(field.form_section_id, [field], {
      success: () => {
        let formElement = this.formService.queryFormElementById(field.id);

        if (formElement) {
          field = formElement;
          if (formElement.id === this.editorState.selectedItem.id) {
            this.editorState.selectedItem = formElement;
            this.editorState.selectedItem.selectableType = SelectableType.PageObject;
          }
        }
      }
    });
  }

  updatePageObject(): void {
    this.documentService
      .updateAndSetPageObject$(
        this.documentService.document.id,
        this.editorState.selectedItemPageObject.id,
        this.editorState.selectedItemPageObject
      )
      .subscribe();
  }

  openPickerForUpload(): void {
    let documentParams = {
      document_id: this.documentService.document.id,
      organization_id: this.documentService.document.organization_id,
      template: false,
      vehicle_id: this.documentService.document.vehicle_id
    };

    this.filestackClient.showDocumentPicker(
      {
        documentParams,
        maxFiles: 1,
        onSuccess: () => {
          window.location.reload();
        },
        uploadParams: documentParams
      },
      {
        accept: '.pdf',
        maxFiles: 1
      }
    );
  }

  selectRadioElement(selectedItem: any): void {
    let checkboxSpecialId = selectedItem.configuration.checkboxSpecialId;
    let element = this.formService.queryFormElementByConfiguration('checkboxSpecialId', checkboxSpecialId);
    element.selectableType = element.type = SelectableType.Element;

    this.editorState.selectedItem = element;
  }

  allVariableKeysExists(content: string = ''): boolean {
    this.organizationKeys = this.getOrganizationKeys();
    this.variablesMissed = getVariablesFrom(content).filter((myKey) => {
      let upKey: string = myKey.toUpperCase();
      return this.organizationKeys.indexOf(upKey) < 0;
    });
    this.variablesMissed = this.variablesMissed.filter((v, i, self) => self.indexOf(v) === i);
    return !this.variablesMissed.length;
  }

  getOrganizationKeys(): string[] {
    return this.formService.formVariables.map((variable: FormVariable) => variable.key);
  }

  isHiddenInput(): boolean {
    let { element_object } = this.editorState.selectedItem || {};
    return element_object.widget_type === WidgetType.Hidden;
  }

  unpublishForm(): void {
    this.formService.unpublishForm({
      success: () => {
        // manually setting active to true since response is not a serialized form
        this.editorState.selectedItem.active = this.formService.form.active = false;
        this.setActionBar();
      }
    });
  }

  get showActionbar(): boolean {
    return !!this.formService.form;
  }

  editable(): boolean {
    return this.formService.form.unlocked_by_user?.id === this.userQuery.getValue().user?.id;
  }

  filterFormattingKeys(value: string): FormattingKeys[] {
    if (!value) return;

    const filterValue = value.toLowerCase();

    return this.FORMATTING_KEYS.filter((option) => option.toLowerCase().includes(filterValue));
  }
}
